import {devtoolsSettings} from "@AppBuilderLib/shared/config/storeSettings";
import {createViewport, IViewportApi} from "@shapediver/viewer.viewport";
import {create} from "zustand";
import {devtools} from "zustand/middleware";
import {IShapeDiverStoreViewport} from "../config/shapediverStoreViewport";
import {ViewportCreateDto} from "../config/viewport";

/**
 * Helper for comparing viewports.
 */
const createViewportIdentifier = function (
	parameters: Pick<ViewportCreateDto, "id">,
) {
	return JSON.stringify({
		id: parameters.id,
	});
};

/**
 * Store data related to the ShapeDiver 3D Viewer Viewport.
 * @see {@link IShapeDiverStoreViewport}
 */
export const useShapeDiverStoreViewport = create<IShapeDiverStoreViewport>()(
	devtools(
		(set, get) => ({
			viewports: {},

			createViewport: async (dto: ViewportCreateDto, callbacks) => {
				// in case a viewport with the same identifier exists, skip creating a new one
				const identifier = createViewportIdentifier(dto);
				const {viewports} = get();

				if (
					Object.values(viewports).findIndex(
						(v) => identifier === createViewportIdentifier(v),
					) >= 0
				)
					return;

				let viewport: IViewportApi | undefined = undefined;

				try {
					viewport = await createViewport(dto);
					if (callbacks?.onCreated) {
						callbacks.onCreated(viewport);
					}
				} catch (e: any) {
					if (callbacks?.onError) {
						callbacks.onError(e);
					}
				}

				set(
					(state) => {
						return {
							viewports: {
								...state.viewports,
								...(viewport ? {[viewport.id]: viewport} : {}),
							},
						};
					},
					false,
					"createViewport",
				);

				return viewport;
			},

			closeViewport: async (viewportId, callbacks) => {
				const {viewports} = get();
				const viewport = viewports[viewportId];
				if (!viewport) return;

				try {
					await viewport.close();
					if (callbacks?.onClosed) {
						callbacks.onClosed(viewportId);
					}
				} catch (e) {
					if (callbacks?.onError) {
						callbacks.onError(e);
					}

					return;
				}

				return set(
					(state) => {
						// create a new object, omitting the session which was closed
						const newViewports: {[id: string]: IViewportApi} = {};
						Object.keys(state.viewports).forEach((id) => {
							if (id !== viewportId)
								newViewports[id] = state.viewports[id];
						});

						return {
							viewports: newViewports,
						};
					},
					false,
					"closeViewport",
				);
			},
		}),
		{...devtoolsSettings, name: "ShapeDiver | Viewer"},
	),
);
